import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FloatingLayer } from '@/components/shared/FloatingLayer';
import { WeaponIcon } from '@/components/shared/WeaponIcon';
import type { WeaponCatalogItem } from '@/types/domain';
import { cn } from '@/utils/cn';

interface WeaponSelectProps {
  value?: string;
  weapons: WeaponCatalogItem[];
  placeholder?: string;
  disabled?: boolean;
  onSelect: (weapon: WeaponCatalogItem | null) => void;
}

function normalizeSearchValue(value: string): string {
  return value.trim().toLowerCase();
}

export function WeaponSelect({
  value,
  weapons,
  placeholder = 'Choose weapon',
  disabled = false,
  onSelect,
}: WeaponSelectProps): JSX.Element {
  const listboxId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedWeapon = weapons.find((weapon) => weapon.id === value) ?? null;
  const normalizedQuery = normalizeSearchValue(query);
  const filteredWeapons = useMemo(
    () =>
      weapons.filter((weapon) => {
        if (!normalizedQuery) {
          return true;
        }

        const haystack = `${weapon.displayName} ${weapon.filename} ${weapon.roleCategory ?? ''}`.toLowerCase();
        return haystack.includes(normalizedQuery);
      }),
    [normalizedQuery, weapons],
  );

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isOpen]);

  const closeMenu = () => {
    setIsOpen(false);
  };

  const commitSelection = (weapon: WeaponCatalogItem | null) => {
    onSelect(weapon);
    closeMenu();
    setQuery('');
    window.requestAnimationFrame(() => {
      triggerRef.current?.focus();
    });
  };

  return (
    <div className={cn('weapon-select', disabled && 'weapon-select--disabled')}>
      <button
        ref={triggerRef}
        type="button"
        className={cn('weapon-select__trigger', isOpen && 'weapon-select__trigger--open')}
        onClick={() => setIsOpen((currentValue) => !currentValue)}
        onKeyDown={(event) => {
          if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && !isOpen) {
            event.preventDefault();
            setIsOpen(true);
          }
        }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-controls={isOpen ? listboxId : undefined}
        aria-expanded={isOpen}
      >
        <div className="weapon-select__value">
          <WeaponIcon imagePath={selectedWeapon?.imagePath} label={selectedWeapon?.displayName ?? placeholder} size="sm" />
          <div>
            <strong>{selectedWeapon?.displayName ?? placeholder}</strong>
            <span>{selectedWeapon?.roleCategory ?? 'Select from the catalog'}</span>
          </div>
        </div>
        <span className="weapon-select__caret" aria-hidden="true">
          <span />
        </span>
      </button>

      <FloatingLayer
        open={isOpen}
        anchorRef={triggerRef}
        id={`${listboxId}-surface`}
        className="weapon-select__menu"
        matchAnchorWidth
        onDismiss={closeMenu}
      >
        {({ placement, maxHeight }) => (
          <>
            <label className="field">
              <span className="field__label">Search Weapons</span>
              <input
                ref={searchInputRef}
                className="field__input"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search by weapon, file, or role"
                autoComplete="off"
              />
            </label>

            <div
              id={listboxId}
              className="weapon-select__options"
              role="listbox"
              aria-label="Weapon options"
              data-placement={placement}
              style={{ maxHeight: Math.max(maxHeight - 88, 120) }}
            >
              <button
                type="button"
                className="weapon-select__option weapon-select__option--clear"
                onClick={() => {
                  commitSelection(null);
                }}
              >
                Clear selection
              </button>

              {filteredWeapons.length > 0 ? (
                filteredWeapons.map((weapon) => {
                  const isSelected = weapon.id === selectedWeapon?.id;

                  return (
                    <button
                      key={weapon.id}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={cn('weapon-select__option', isSelected && 'weapon-select__option--selected')}
                      onClick={() => {
                        commitSelection(weapon);
                      }}
                    >
                      <WeaponIcon imagePath={weapon.imagePath} label={weapon.displayName} size="sm" />
                      <div className="weapon-select__option-content">
                        <strong>{weapon.displayName}</strong>
                        <span>
                          {weapon.roleCategory ?? 'Needs review'}
                          {!weapon.isActive ? ' - Inactive' : ''}
                        </span>
                      </div>
                    </button>
                  );
                })
              ) : (
                <p className="weapon-select__empty">No weapons matched your search.</p>
              )}
            </div>
          </>
        )}
      </FloatingLayer>
    </div>
  );
}
